<template>
  <div class="update-html">
    <!-- 左侧选择区域 -->
    <div class="left-panel">
      <div class="selectors">
        <!-- 期刊选择 -->
        <div class="selector-item">
          <label>期刊:</label>
          <div class="select-container" ref="journalSelect">
            <div 
              class="select-options" 
              @scroll="handleJournalScroll"
              ref="journalOptions"
            >
              <div 
                v-for="journal in journals" 
                :key="journal.id"
                class="select-option"
                :class="{ active: selectedJournal && selectedJournal.id === journal.id }"
                @click="selectJournal(journal)"
              >
                {{ journal.abbreviation || journal.name || '未命名期刊' }}
              </div>
              <div v-if="isLoadingJournals" class="loading">加载中...</div>
            </div>
          </div>
        </div>

        <!-- 卷选择 -->
        <div class="selector-item" v-if="selectedJournal">
          <label>卷:</label>
          <div class="select-container" ref="volumeSelect">
            <div 
              class="select-options" 
              @scroll="handleVolumeScroll"
              ref="volumeOptions"
            >
              <div 
                v-for="volume in volumes" 
                :key="volume.id"
                class="select-option"
                :class="{ active: selectedVolume && selectedVolume.id === volume.id }"
                @click="selectVolume(volume)"
              >
                {{ volume.volume }}
              </div>
              <div v-if="isLoadingVolumes" class="loading">加载中...</div>
            </div>
          </div>
        </div>

        <!-- 文章选择 -->
        <div class="selector-item" v-if="selectedVolume">
          <label>文章:</label>
          <div class="select-container" ref="articleSelect">
            <div 
              class="select-options" 
              @scroll="handleArticleScroll"
              ref="articleOptions"
            >
              <div 
                v-for="article in articles" 
                :key="article.id"
                class="select-option"
                :class="{
                  active: selectedArticle && selectedArticle.id === article.id,
                  confirmed: article.is_confirm_body
                }"
                @click="selectArticle(article)"
              >
                <div class="article-info">
                  <span class="article-id">{{ article.paper_id }}</span>
                  <span 
                    class="confirm-status" 
                    :class="{ confirmed: article.is_confirm_body }"
                    :title="article.is_confirm_body ? '已确认' : '未确认'"
                  >
                    {{ article.is_confirm_body ? '✓' : '?' }}
                  </span>
                </div>
              </div>
              <div v-if="isLoadingArticles" class="loading">加载中...</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 右侧编辑区域 -->
    <div class="right-panel">
      <div class="content-editor">
        <div class="editor-toolbar">
          <button 
            class="preview-btn" 
            @click="openPreview" 
            :disabled="!selectedArticle?.web_url"
            title="在新窗口中预览"
          >
            打开页面
          </button>
          <button 
            class="archive-btn" 
            @click="confirmArticle"
            :disabled="isConfirming"
            title="确认并存档文章"
          >
            {{ isConfirming ? '存档中...' : '存档' }}
          </button>
          <span class="article-id-display" v-if="selectedArticle">
            ID: {{ selectedArticle.id }}
          </span>
          <button 
            class="save-btn"
            @click="saveContent" 
            :disabled="!hasChanges"
          >
            保存更新
          </button>
          <span v-if="saveStatus" :class="saveStatus.type">{{ saveStatus.message }}</span>
        </div>
        <!-- 移除自定义的预览区域，只保留编辑器 -->
        <div class="editor-container">
          <Editor
            api-key="o5ope1swx59364m9hnxpl5lqqve0lo6p1s4raugjdcxcubs3"
            v-model="articleContent"
            :init="editorConfig"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import Editor from '@tinymce/tinymce-vue'
import { ref, onMounted, computed } from 'vue'
import '../assets/article-style.css'
import { useRoute } from 'vue-router'
import apiService from '../api'

// 定义图片上传处理函数
function handleImageUpload(blobInfo, success, failure) {
  apiService.uploadImage(blobInfo.blob())
    .then(result => {
      if (result.success && result.data.file_url) {
        success(result.data.file_url)
      } else {
        failure('上传失败: ' + (result.message || '未知错误'))
      }
    })
    .catch(error => {
      console.error('图片上传失败:', error)
      failure('上传失败: ' + error.message)
    })
}

// 状态定义
const journals = ref([])
const volumes = ref([])
const articles = ref([])
const selectedJournal = ref(null)
const selectedVolume = ref(null)
const selectedArticle = ref(null)
const articleContent = ref('')
const originalContent = ref('')
const saveStatus = ref(null)

// 添加分页相关的 ref
const journalPage = ref(1)
const volumePage = ref(1)
const articlePage = ref(1)

// 添加加载状态
const isLoadingJournals = ref(false)
const isLoadingVolumes = ref(false)
const isLoadingArticles = ref(false)

// 添加是否还有更多数据的标记
const hasMoreJournals = ref(true)
const hasMoreVolumes = ref(true)
const hasMoreArticles = ref(true)

// 从路由获取文章 ID
const route = useRoute()

// 获取期刊列表
const fetchJournals = async (page = 1, append = false) => {
  if (!hasMoreJournals.value || isLoadingJournals.value) return
  
  isLoadingJournals.value = true
  try {
    const data = await apiService.getJournals(page, 20)
    if (data.success) {
      if (Array.isArray(data.data.list)) {
        if (append) {
          journals.value = [...journals.value, ...data.data.list]
        } else {
          journals.value = data.data.list
        }
        hasMoreJournals.value = page < data.data.total_pages
        journalPage.value = page
      }
    }
  } catch (error) {
    console.error('获取期刊列表失败:', error)
  } finally {
    isLoadingJournals.value = false
  }
}

// 获取卷列表
const fetchVolumes = async (journalId, page = 1, append = false) => {
  if (!hasMoreVolumes.value || isLoadingVolumes.value) return

  isLoadingVolumes.value = true
  try {
    const data = await apiService.getVolumes(journalId, page, 20)
    if (data.success) {
      if (append) {
        volumes.value = [...volumes.value, ...data.data.list]
      } else {
        volumes.value = data.data.list
      }
      hasMoreVolumes.value = page < data.data.total_pages
      volumePage.value = page
    }
  } catch (error) {
    console.error('获取卷列表失败:', error)
  } finally {
    isLoadingVolumes.value = false
  }
}

// 获取文章列表
const fetchArticles = async (volumeId, page = 1, append = false) => {
  if (!hasMoreArticles.value || isLoadingArticles.value) return

  isLoadingArticles.value = true
  try {
    const data = await apiService.getArticles(volumeId, page, 20)
    if (data.success) {
      if (append) {
        articles.value = [...articles.value, ...data.data.list]
      } else {
        articles.value = data.data.list
      }
      hasMoreArticles.value = page < data.data.total_pages
      articlePage.value = page
    }
  } catch (error) {
    console.error('获取文章列表失败:', error)
  } finally {
    isLoadingArticles.value = false
  }
}

// 保存文章内容
const saveContent = async () => {
  const articleId = selectedArticle.value?.id || route.query.article_id
  if (!articleId) return

  try {
    const data = await apiService.saveArticleBody(articleId, articleContent.value)
    if (data.success) {
      saveStatus.value = { type: 'success', message: '保存成功' }
      originalContent.value = articleContent.value
    } else {
      saveStatus.value = { type: 'error', message: '保存失败' }
    }

    setTimeout(() => {
      saveStatus.value = null
    }, 3000)
  } catch (error) {
    console.error('保存文章内容失败:', error)
    saveStatus.value = { type: 'error', message: '保存失败' }
  }
}

// 添加滚动处理函数
const handleJournalScroll = (e) => {
  const { scrollTop, scrollHeight, clientHeight } = e.target
  if (scrollHeight - scrollTop - clientHeight < 50) {
    fetchJournals(journalPage.value + 1, true)
  }
}

const handleVolumeScroll = (e) => {
  const { scrollTop, scrollHeight, clientHeight } = e.target
  if (scrollHeight - scrollTop - clientHeight < 50) {
    fetchVolumes(selectedJournal.value.id, volumePage.value + 1, true)
  }
}

const handleArticleScroll = (e) => {
  const { scrollTop, scrollHeight, clientHeight } = e.target
  if (scrollHeight - scrollTop - clientHeight < 50) {
    fetchArticles(selectedVolume.value.id, articlePage.value + 1, true)
  }
}

// 修改选择文章的函数
const selectArticle = async (article) => {
  try {
    const data = await apiService.getArticleBody(article.id)
    if (data.success) {
       // 处理 UTF-8 编码的特殊字符组合
       let processedContent = data.data.body_html
          .replace(/\u00e2\u0080\u0099/g, "'")     // 单引号
          .replace(/\u00e2\u0080\u009c/g, '"')     // 左双引号
          .replace(/\u00e2\u0080\u009d/g, '"')     // 右双引号
          .replace(/\u00e2\u0080\u0094/g, '—')     // 破折号
          .replace(/\u00e2\u0080\u0093/g, '–');    // 短破折号

        // 设置处理后的内容
        articleContent.value = processedContent;
        originalContent.value = processedContent;
   
      
      selectedArticle.value = {
        ...article,
        web_url: data.data.web_url
      }
    }
  } catch (error) {
    console.error('获取文章内容失败:', error)
  }
}

// 修改选择处理函数
const selectJournal = async (journal) => {
  selectedJournal.value = journal
  selectedVolume.value = null
  selectedArticle.value = null
  volumes.value = []
  articles.value = []
  volumePage.value = 1
  hasMoreVolumes.value = true
  await fetchVolumes(journal.id, 1)
}

const selectVolume = async (volume) => {
  selectedVolume.value = volume
  selectedArticle.value = null
  articles.value = []
  articlePage.value = 1
  hasMoreArticles.value = true
  await fetchArticles(volume.id, 1)
}

// 计算是否有更改
const hasChanges = computed(() => {
  return articleContent.value !== originalContent.value
})

// 添加中文标点符号检测函数
const isChinesePunctuation = (char) => {
  // 匹配常见的中文标点符号
  const chinesePunctuationRegex = /[\u3000-\u303F\uFF00-\uFFEF]/;
  return chinesePunctuationRegex.test(char);
};

// 编辑器配置
const editorConfig = {
  height: '100%',
  menubar: false,
  toolbar_mode: 'sliding',
  plugins: [
    'code anchor autolink charmap codesample emoticons image link lists media searchreplace table visualblocks wordcount fullscreen'
  ],
  toolbar: [
    'undo redo | code formatselect | bold italic underline strikethrough | forecolor backcolor',
    'alignleft aligncenter alignright alignjustify | bullist numlist outdent indent',
    'link image media table | removeformat fullscreen | mathinline mathblock | custompreview'
  ],
  content_style: `
    /* 导入公共样式 */
    .mce-content-body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
      font-size: 14px;
      line-height: 1.6;
      padding: 20px;
    }
    .mce-content-body p {
      margin: 1em 0;
    }
    .mce-content-body a {
      color: #1a1a1a;
    }
    .mce-content-body img {
      max-width: 100%;
    }
    .mce-content-body table {
      border-collapse: collapse;
      margin: 1em 0;
      width: 100%;
    }
    .mce-content-body table td,
    .mce-content-body table th {
      border: 1px solid #ddd;
      padding: 8px;
    }
    /* 数学公式样式 */
    .math {
      font-size: 16px;
      margin: 10px 0;
    }
    .math-inline {
      display: inline-block;
      margin: 0 2px;
    }
    /* 添加数学公式样式 */
    .MathJax_Display {
      margin: 0.5em 0 !important;
    }
    .MathJax {
      color: #333 !important;
    }
    p > span {
      color: inherit !important;
    }
    .math, .MathJax, .MathJax_CHTML {
      font-size: inherit !important;
      color: inherit !important;
    }
  `,
  branding: false,
  promotion: false,
  language: 'zh_CN',
  images_upload_url: 'https://press.eliwise.org/api/markdown/upload',
  images_upload_handler: handleImageUpload,
  extended_valid_elements: 'script[type|src|async|defer],span[class|style|id],div[class|style|id]',
  custom_elements: 'script,span,div',
  verify_html: false,
  entity_encoding: 'raw',
  encoding: 'utf-8',
  doctype: '<!DOCTYPE html>',
  document_base_url: window.location.origin,
  paste_remove_styles: true,
  paste_as_text: false,
  paste_enable_default_filters: true,
  formats: {
    removeformat: [
      {
        selector: '*',
        remove: 'all',
        split: true,
        expand: false,
        block_expand: true,
        deep: true
      }
    ]
  },
  schema: 'html5',
  element_format: 'html',
  paste_data_images: true,
  setup: function(editor) {
    // 添加内容预处理
    editor.on('BeforeSetContent', function(e) {
      if (e.content) {
        // 打印所有中文标点符号
        console.log('检测到的中文标点符号：');
        const chars = Array.from(e.content);
        chars.forEach(char => {
          if (isChinesePunctuation(char)) {
            console.log(`字符: "${char}", Unicode: ${char.charCodeAt(0).toString(16)}`);
          }
        });

        // 打印特殊字符
        console.log('特殊字符：');
        const specialChars = e.content.match(/[""''—–]/g) || [];
        specialChars.forEach(char => {
          console.log(`字符: "${char}", Unicode: ${char.charCodeAt(0).toString(16)}`);
        });

        // 处理内容
        e.content = e.content
          // 处理中文标点
          .replace(/[\u3000-\u303F]/g, char => {
            console.log(`替换中文标点: ${char} -> ${char.charCodeAt(0).toString(16)}`);
            return char;
          })
          // 处理全角标点
          .replace(/[\uFF00-\uFFEF]/g, char => {
            console.log(`替换全角标点: ${char} -> ${char.charCodeAt(0).toString(16)}`);
            return char;
          });
      }
    });

    // 添加内容后处理
    editor.on('GetContent', function(e) {
      if (e.content) {
        // 打印处理后的内容中的特殊字符
        console.log('处理后的内容中的特殊字符：');
        const chars = Array.from(e.content);
        chars.forEach(char => {
          if (isChinesePunctuation(char)) {
            console.log(`字符: "${char}", Unicode: ${char.charCodeAt(0).toString(16)}`);
          }
        });
      }
    });

    // 监听内容变化和初始化
    editor.on('init', function() {
      // 初始化时渲染公式
      setTimeout(() => {
        renderMathJax()
      }, 1000)

      // 监听内容变化
      editor.on('Change KeyUp NodeChange SetContent', function() {
        renderMathJax()
      })
    })
  }
}

// 修改渲染函数
function renderMathJax() {
  if (window.MathJax) {
    try {
      window.MathJax.typesetClear()
      window.MathJax.typesetPromise()
    } catch (e) {
      console.error('MathJax rendering error:', e)
    }
  }
}

// 添加预览函数
const openPreview = () => {
  if (selectedArticle.value?.web_url) {
    const newUrl = selectedArticle.value.web_url.replace(
      "read.ewapublishing.org",
      "direct.ewa.pub"
    )
    window.open(newUrl, '_blank')
  }
}

onMounted(async () => {
  const articleId = route.query.article_id
  if (articleId) {
    try {
      const data = await apiService.getArticleBody(articleId)
      if (data.success) {
        // 处理 UTF-8 编码的特殊字符组合
        let processedContent = data.data.body_html
          .replace(/\u00e2\u0080\u0099/g, "'")     // 单引号
          .replace(/\u00e2\u0080\u009c/g, '"')     // 左双引号
          .replace(/\u00e2\u0080\u009d/g, '"')     // 右双引号
          .replace(/\u00e2\u0080\u0094/g, '—')     // 破折号
          .replace(/\u00e2\u0080\u0093/g, '–');    // 短破折号

        // 设置处理后的内容
        articleContent.value = processedContent;
        originalContent.value = processedContent;

        // 设置文章基本信息
        selectedArticle.value = {
          id: articleId,
          web_url: data.data.web_url,
          journal_id: data.data.journal_id,
          volume_id: data.data.volume_id
        }

        // 获取期刊列表
        await fetchJournals()

        // 找到对应的期刊
        const journal = journals.value.find(j => j.id === data.data.journal_id)
        if (journal) {
          // 选择期刊并获取卷列表
          selectedJournal.value = journal
          await fetchVolumes(journal.id)

          // 找到对应的卷
          const volume = volumes.value.find(v => v.id === data.data.volume_id)
          if (volume) {
            // 选择卷并获取文章列表
            selectedVolume.value = volume
            await fetchArticles(volume.id)

            // 找到当前文章并更新完整信息
            const article = articles.value.find(a => a.id === articleId)
            if (article) {
              selectedArticle.value = {
                ...article,
                id: articleId,
                web_url: data.data.web_url
              }
            }
          }
        }
      }
    } catch (error) {
      console.error('获取文章信息失败:', error)
    }
  } else {
    // 没有文章 ID 时，正常加载期刊列表
    fetchJournals()
  }
})
</script>

<style scoped>
.update-html {
  display: flex;
  gap: 20px;
  padding: 20px;
  height: calc(100vh - 100px); /* 减去导航栏和padding的高度 */
}

.left-panel {
  width: 300px;
  padding: 20px;
  background: #f5f5f5;
  border-radius: 8px;
}

.right-panel {
  flex: 1;
  overflow: hidden;
}

.selectors {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.selector-item {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.selector-item label {
  font-weight: bold;
  color: #333;
}

select {
  padding: 8px;
  width: 100%;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.content-editor {
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.editor-toolbar {
  display: flex;
  gap: 10px;
  align-items: center;
  padding: 10px;
  background: #f5f5f5;
  border-radius: 4px;
}

.editor-container {
  height: calc(100% - 60px); /* 减去toolbar的高度 */
}

.vditor {
  height: 100%;
}

/* 调整 Vditor 的样式 */
:deep(.vditor) {
  border: none;
}

:deep(.vditor-toolbar) {
  border-bottom: 1px solid #ddd;
  background-color: #f5f5f5;
}

:deep(.vditor-sv) {
  padding: 10px 20px !important;
  color: #333 !important;
}

:deep(.vditor-sv .vditor-sv__marker) {
  color: #333 !important;
}

:deep(.vditor-sv .vditor-sv__content) {
  color: #333 !important;
}

:deep(.vditor-sv__content[contenteditable="true"]) {
  color: #333 !important;
}

:deep(.vditor-sv) {
  font-size: 16px !important;
}

:deep(.vditor-preview) {
  border-left: 1px solid #ddd;
  padding: 10px 20px !important;
}

:deep(.vditor-splitview) {
  display: flex !important;
}

:deep(.vditor-splitview .vditor-sv) {
  width: 50% !important;
}

:deep(.vditor-splitview .vditor-preview) {
  width: 50% !important;
  border-left: 1px solid #ddd;
}

button {
  padding: 8px 16px;
  background-color: #42b983;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s ease;
}

button:hover {
  background-color: #3aa876;
}

button:disabled {
  background-color: #a8d5c2;
  cursor: not-allowed;
}

.success {
  color: #42b983;
}

.error {
  color: #ff4444;
}

.selector-item span {
  font-size: 14px;
  color: #666;
  margin-top: 5px;
}

.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-top: 5px;
}

.pagination button {
  padding: 4px 8px;
  font-size: 12px;
  background-color: #fff;
  border: 1px solid #ddd;
  color: #333;
}

.pagination button:disabled {
  background-color: #f5f5f5;
  color: #999;
}

.pagination span {
  font-size: 12px;
  color: #666;
}

/* 确保预览区域内的公共样式生效 */
.preview :deep(p) {
  margin: 1em 0;
}

.preview :deep(img) {
  max-width: 100%;
}

.select-container {
  position: relative;
  height: 200px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
}

.select-options {
  height: 100%;
  overflow-y: auto;
  padding: 5px 0;
}

.select-option {
  padding: 8px 12px;
  cursor: pointer;
  transition: background-color 0.2s;
  display: flex;
  align-items: center;
}

.select-option:hover {
  background-color: #f0f0f0;
}

.select-option.active {
  background-color: #e6f7ff;
  color: #1890ff;
}

.select-option.confirmed {
  border-left: 3px solid #52c41a;
}

.select-option.confirmed .article-id {
  font-weight: 500;
}

.loading {
  text-align: center;
  padding: 10px;
  color: #999;
  font-size: 14px;
}

/* 自定义滚动条样式 */
.select-options::-webkit-scrollbar {
  width: 6px;
}

.select-options::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.select-options::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 3px;
}

.select-options::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.article-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.article-id {
  flex: 1;
}

.confirm-status {
  margin-left: 8px;
  font-size: 12px;
  padding: 2px 6px;
  border-radius: 10px;
  background-color: #f0f0f0;
  color: #999;
}

.confirm-status.confirmed {
  background-color: #e6f7ff;
  color: #1890ff;
}

.editor-container {
  height: calc(100% - 60px);
}

:deep(.tox-tinymce) {
  border: none !important;
  border-radius: 0 !important;
}

:deep(.tox-editor-container) {
  background: #fff;
}

.preview-btn {
  background-color: #4a90e2;
}

.preview-btn:hover {
  background-color: #357abd;
}

.preview-btn:disabled {
  background-color: #9dc3e6;
}

.article-id-display {
  padding: 6px 12px;
  background-color: #f5f5f5;
  border-radius: 4px;
  color: #666;
  font-size: 14px;
  margin: 0 10px;
  border: 1px solid #ddd;
}
</style> 